import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 協会概要ページ 全面作り直し（Implementer A スコープ） =====
// デザイン土台: src/pages/home/page.tsx の新スタイル（紺#1a2942 / 真鍮金#a38e65 / BTN-08 2状態 /
//   紺帯番号バンド / 金差し色見出し / リード3行+ / 細罫エディトリアル行 / 白リッチカード）を精読し、
//   同じ言語の「新バリエーション」を設計（トップと同一セクション型の使い回しはしない）。
// 事実正典（創作禁止・出典path記録）:
//   [S1] mng-thebutler/00_事業説明.md L34-40,L59-65 / home L105-112
//   [S2] src/pages/about/access/page.tsx L83-100 / mng-thebutler/00 L100-104,L39
//   [S3] mng-thebutler/00_事業説明.md L19-27（理念・提供価値）
//   [S4] src/pages/about/overview(旧) / home II-VII（事業領域）/ mng-thebutler/00 L52-55
//   [S5] src/pages/about/history/page.tsx（年表）/ mng-thebutler/00 L34-36
// ここに無い年号・数値は書かない。

export default function AboutOverviewPage() {
  useEffect(() => {
    updateSEO(seoData['/about/overview']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: '協会概要' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  // BTN-08（home と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [S2] 法人概要 定義リスト（出典: access L83-100 / mng-thebutler/00 L100-104,L39）
  const corporateFacts = [
    { label: '法人名', value: '一般社団法人 全日本執事協会' },
    { label: '設立', value: '1890年（明治23年）' },
    { label: '代表理事', value: '石井 裕一（三代目頭取）' },
    { label: '所在地', value: '〒135-0064 東京都江東区青海2丁目7−4' },
    { label: '法人番号', value: '2010005039584' },
    { label: '電話', value: '03-6824-4086（ご来訪は完全予約制）' },
    { label: '主な事業', value: '執事の人材育成・研修／資格認定制度の運営／セミナー・イベント開催／国際交流／出版・情報発信' },
    { label: '拠点の特色', value: 'お台場に、中世ヨーロッパの古城の一室を再現した事務所兼「執事サロン」を構える' },
  ];

  // [S4] 事業領域（出典: 旧overview / home各章 / mng-thebutler/00 L52-55）
  const businessFields = [
    { no: '01', en: 'Human Resources', ja: '人材育成・研修', desc: '執事としての基本技能から専門技術まで、体系的な研修プログラムを通じて質の高い執事を育成します。お台場の執事サロンを実地研鑽の場とし、伝統を次世代へ継承します。' },
    { no: '02', en: 'Certification', ja: '資格認定制度', desc: 'アソシエイト・プロフェッショナル・マスターの三段階の資格に加え、食事・旅行・ビジネスマナーなどの専門資格を整備。技能を客観的に証明し、サービスの質を担保します。' },
    { no: '03', en: 'Seminars', ja: 'セミナー・イベント', desc: '企業や個人を対象に、リーダーシップ・ホスピタリティ・コミュニケーションといったテーマの実践的なセミナーを開催。執事の所作と考え方をビジネスの現場へ届けます。' },
    { no: '04', en: 'International', ja: '国際交流', desc: 'グローバルな視野で執事文化の発展に貢献することをめざし、国際的に通用する執事の育成に取り組みます。海外からのお客様への対応力も重視しています。' },
    { no: '05', en: 'Publishing', ja: '出版・情報発信', desc: '専門刊行物や書籍の発行、SNS・YouTube「執事ベル」を通じた発信により、執事文化の「いま」を社会へ広く伝えます。' },
  ];

  // [S5] 沿革ダイジェスト（出典: about/history/page.tsx / mng-thebutler/00 L34-36）
  const historyDigest = [
    { year: '1890', label: '協会の創設', text: '明治維新後の文明開化のなか、創設者・田中慎吾が英国留学で学んだ執事文化を日本に導入し、協会を創設。' },
    { year: '1903', label: '養成プログラム開始', text: '初の体系的な執事養成プログラムを開講。西洋式のサービスマナーと日本の礼儀作法を融合した独自カリキュラムを確立。' },
    { year: '1955', label: '地域協会の設立', text: '関東執事協会と関西執事協会が相次いで設立。地域特性に応じたサービスと全国的なネットワークの構築を開始。' },
    { year: '2000', label: '協会の再統合', text: '分立していた関東・関西の執事協会が再統合され、全国統一組織として新たなスタートを切る。' },
    { year: '2023', label: '三代目頭取の就任', text: '石井裕一が三代目頭取として就任。デジタル化と国際連携を通じた「執事文化の現代化」を推進。' },
    { year: '2025', label: '創立135周年', text: '協会創立135周年を迎え、これまでの歩みを振り返りながら次の時代への展望を描く。' },
  ];

  return (
    <Layout>
      {/* SEO は updateSEO(seoData['/about/overview']) を単一の正とする（JSX 二重定義は除去・H3） */}
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左寄せテキスト）。
           由来: home ヒーローの全幅ビルボード文法を下層概要ページへ適用（ABOUT-F の2カラム型とは別型）===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-space-salonhall-01.webp"
          alt="全日本執事協会 本部。歴史ある調度に囲まれた協会の応接空間"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Association Overview</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">135年、日本に</span>
              <span className="block">執事文化を育んで</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創設以来、日本における執事文化の普及と発展に努めてきました。ヨーロッパの執事の伝統と、日本古来の「おもてなし」の精神を融合させ、世界に通用するプロフェッショナルな執事を育てています。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* サイドナビ（共通コンポーネント・overviewを強調） */}
            <AboutSideNav items={aboutNav} activeHref="/about/overview" />

            {/* コンテンツ列 */}
            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- S1. 概要リード（金eyebrow+金差し色見出し+リード+細罫データ行）。由来: home II リード+細罫データ行 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Who We Are</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  日本で唯一の、<span className="text-[#a38e65]">執事専門機関</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    全日本執事協会は、130年以上の系譜を持つ日本唯一の執事専門機関です。単なる家事の代行ではなく、英国式の規律と品格、そして日本独自の細やかな礼節を兼ね備えたプロフェッショナルが、お客様のプライベートからビジネスまでを包括的に支えます。
                  </p>
                  <p>
                    私たちが提供するのは、人手の代替ではありません。段取りや雑事から解放され、本来向き合うべき「仕事」「家族」「休息」に集中できる環境そのものです。執事は家庭の最も内側に入り、暮らしの基盤を静かに整える「家の心臓」となることを目指します。
                  </p>
                  <p>
                    お台場に構える事務所兼「執事サロン」は、中世ヨーロッパの古城の一室を再現した、実在する教育・体験の拠点です。オンラインのみで完結する事業とは一線を画す、物理的なサービス品質の担保がここにあります。
                  </p>
                </div>
                {/* 細罫データ行 */}
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">創設</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">歴史</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">継承</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3<span className="text-sm text-[#8f7a56] ml-1">代</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">本部</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">東京・お台場</dd>
                  </div>
                </dl>
              </section>

              {/* --- S2. 法人概要（定義リスト/罫線テーブル型）= 新バリエーション①
                   由来注記: home VII 会員種別「細罫テーブル調」の罫線文法を、1カラムの定義リスト(dl)へ翻案。
                   トップに定義リスト型は無いため新造。読者が最初に確認したい事実を先出しする安心先行型。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Corporate Profile</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  法人概要
                </h2>
                <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
                  <dl className="border-t-2 border-[#1a2942]">
                    {corporateFacts.map((row) => (
                      <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                        <dt className="sm:w-[124px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                        <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-interior-saloncorner-02.webp"
                      alt="中世ヨーロッパの様式を再現した執事サロンの一隅"
                      className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* --- S3. 私たちの目的（引用大組み型）= 新バリエーション②
                   由来注記: home ABOUT-F の「紺パネル+左罫引用」を、引用を主役に据えた大組みへ拡張。
                   紺全面パネルに理念の一文を大きく組み、下に3つの提供価値を横並べ。トップに引用主役型は無いため新造。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Our Purpose</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  私たちが大切にしていること
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14 lg:py-16">
                  <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
                  <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[30px] leading-[1.65] tracking-[0.02em] max-w-3xl">
                    日本のおもてなしの精神と、西洋式執事サービスの融合。主人が言葉にする前にニーズを察知し、空気のように自然に動く「先回りの奉仕」を、私たちは執事の本質と考えます。
                  </blockquote>
                  <div className="w-11 h-px bg-[#a38e65] mt-8 mb-10"></div>
                  <div className="grid sm:grid-cols-3 gap-8 lg:gap-10">
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value i.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">時間と心の余裕</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">雑事から解放し、仕事・家族・休息に集中できる環境そのものをお届けします。</p>
                    </div>
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value ii.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">家の心臓として</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">暮らしの最も内側に入り、生活の基盤を静かに整え、一族の機微に寄り添います。</p>
                    </div>
                    <div>
                      <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Value iii.</div>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">見えない奉仕</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">目立たずとも確かに支える、先回りのおもてなしを追求します。</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* --- S4. 事業領域（番号付き2カラムブロック型）
                   由来: home の紺帯番号バンド（金ローマ/番号+和見出し）の文法を、番号付きの2カラム説明ブロックへ応用。
                   home IV の単一行エディトリアル（価格列付き）とは別型（列構成が異なる）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. What We Do</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  協会の<span className="text-[#a38e65]">5つの事業領域</span>
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  執事文化の普及と発展を、人を育てること・技能を証明すること・学びを広げること・世界とつながること・言葉で伝えることの5つの柱で進めています。それぞれが連携し、質の高い執事サービスを社会に届けます。
                </p>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  {businessFields.map((f) => (
                    <div key={f.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{f.no}</span>
                      <div>
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{f.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- S5. 沿革ダイジェスト（縦タイムライン細罫型）= 新バリエーション③
                   由来注記: 新造。トップに時系列（タイムライン）型は無い。左に年号・縦の真鍮罫・右に出来事。
                   詳細は沿革ページへ導線。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Heritage</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  歩みのダイジェスト
                </h2>
                <div className="grid lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-start">
                  <ol className="relative border-l border-[#e2ddd2] ml-1">
                    {historyDigest.map((h) => (
                      <li key={h.year} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                        <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                        <div className="flex items-baseline gap-3 mb-1.5">
                          <span className="font-['Playfair_Display',serif] text-xl text-[#1a2942]">{h.year}</span>
                          <span className="jba-display font-medium text-[#8f7a56] text-[15px] sm:text-base">{h.label}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{h.text}</p>
                      </li>
                    ))}
                  </ol>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-still-heritage-01.webp"
                      alt="協会の歩みを物語る調度品"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30 mb-5"
                    />
                    <a href="/about/history" className={`${BTN08_ON_LIGHT} w-full`}>沿革・歴史をすべて見る</a>
                  </div>
                </div>
              </section>

              {/* --- S6. 資格・サービスへの導線（紺CTAパネル型）。由来: home 締めCTAパネル --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  協会をもっと知る
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    全日本執事協会は、執事の育成と資格認定、実践的なセミナー、そして執事人材サービス「THE BUTLER」の運営を通じて、執事文化を社会に根づかせています。関心をお持ちの方は、資格制度やサービスの一覧もぜひご覧ください。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/butler-services/" className={BTN08_ON_NAVY}>執事サービスを見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
